(function () {
  'use strict';

  const modelContext = document.modelContext || (typeof navigator !== 'undefined' ? navigator.modelContext : null);
  if (!modelContext?.registerTool || window.__zentrobadWebMcpRegistered) return;
  window.__zentrobadWebMcpRegistered = true;

  const jsonHeaders = { Accept: 'application/json' };
  const registrationController = new AbortController();

  const normalize = (value) =>
    String(value || '')
      .trim()
      .toLocaleLowerCase('de-DE')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const money = (cents, currency = window.Shopify?.currency?.active || 'EUR') => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value);
    } catch (_) {
      return `${value.toFixed(2)} ${currency}`;
    }
  };

  const asResult = (payload) => JSON.stringify(payload);

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { ...jsonHeaders, ...(options.headers || {}) },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || `Shopify-Anfrage fehlgeschlagen (${response.status}).`);
    }
    return data;
  };

  const productPath = () => {
    const match = window.location.pathname.match(/^\/products\/[^/]+/);
    return match ? match[0] : '';
  };

  const loadProduct = async () => {
    const path = productPath();
    if (!path) throw new Error('Dieses Werkzeug ist nur auf einer Produktseite verfügbar.');
    return requestJson(`${path}.js`);
  };

  const optionName = (option) => (typeof option === 'string' ? option : option?.name || '');
  const optionValues = (option) => (Array.isArray(option?.values) ? option.values : []);

  const optionKey = (name) => {
    const value = normalize(name);
    if (value.includes('breite') || value.includes('masse')) return 'width';
    if (value.includes('unterschrank') || value.includes('mobelfarbe')) return 'furnitureColor';
    if (value.includes('griff')) return 'gripType';
    if (value.includes('waschbecken') || value.includes('beckenfarbe')) return 'basinColor';
    if (value === 'farbe') return 'color';
    return `option${value.replace(/\s+/g, '_')}`;
  };

  const currentVariantId = () => {
    const fromUrl = new URL(window.location.href).searchParams.get('variant');
    if (fromUrl) return String(fromUrl);
    const input = document.querySelector('#MainContent product-form-component input[name="id"]');
    return input?.value ? String(input.value) : '';
  };

  const selectedOptions = (product, variant) =>
    Object.fromEntries(
      (product.options || []).map((option, index) => [optionName(option), variant?.options?.[index] || ''])
    );

  const conciseVariant = (product, variant) => ({
    id: String(variant.id),
    title: variant.title,
    available: Boolean(variant.available),
    price: money(variant.price),
    options: selectedOptions(product, variant),
  });

  const findCurrentVariant = (product) => {
    const id = currentVariantId();
    return product.variants.find((variant) => String(variant.id) === id) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];
  };

  const listChoices = (product) =>
    Object.fromEntries(
      (product.options || []).map((option, index) => {
        const values = optionValues(option).length
          ? optionValues(option)
          : [...new Set(product.variants.map((variant) => variant.options?.[index]).filter(Boolean))];
        return [optionName(option), values];
      })
    );

  const register = (tool) =>
    modelContext.registerTool(tool, { signal: registrationController.signal }).catch((error) => {
      console.warn(`[Zentrobad WebMCP] ${tool.name} konnte nicht registriert werden.`, error);
    });

  register({
    name: 'search_products',
    title: 'Produkte suchen',
    description: 'Durchsucht den lieferbaren Zentrobad-Katalog nach einem freien Suchbegriff und liefert bis zu acht passende Produkte mit Preis und direktem Link.',
    inputSchema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Suchbegriff, etwa Waschbecken A4, Hochschrank oder Spiegel 80 cm.' },
      },
      required: ['query'],
      additionalProperties: false,
    },
    annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
    execute: async ({ query }) => {
      const term = String(query || '').trim();
      if (term.length < 2) return asResult({ ok: false, error: 'Bitte mindestens zwei Zeichen als Suchbegriff angeben.' });
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'hide',
      });
      const data = await requestJson(`/search/suggest.json?${params}`);
      const products = data.resources?.results?.products || [];
      return asResult({
        ok: true,
        query: term,
        count: products.length,
        products: products.map((product) => ({
          title: product.title,
          price: product.price,
          available: product.available !== false,
          url: new URL(product.url, window.location.origin).href,
        })),
      });
    },
  });

  register({
    name: 'get_cart',
    title: 'Warenkorb lesen',
    description: 'Liest den aktuellen Shopify-Warenkorb und liefert Artikel, Mengen, Einzelpreise und Gesamtsumme. Der Warenkorb wird dabei nicht verändert.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
    execute: async () => {
      const cart = await requestJson('/cart.js');
      return asResult({
        ok: true,
        itemCount: cart.item_count,
        total: money(cart.total_price, cart.currency),
        checkoutReady: cart.item_count > 0,
        items: cart.items.map((item) => ({
          product: item.product_title,
          variant: item.variant_title,
          quantity: item.quantity,
          linePrice: money(item.final_line_price, cart.currency),
          url: new URL(item.url, window.location.origin).href,
        })),
        cartUrl: new URL('/cart', window.location.origin).href,
      });
    },
  });

  if (!productPath()) return;

  register({
    name: 'get_product',
    title: 'Produktdaten lesen',
    description: 'Liest das aktuell geöffnete Produkt, die ausgewählte Variante sowie alle verfügbaren Auswahlwerte für Maße, Farbe und Griffform.',
    inputSchema: { type: 'object', properties: {}, additionalProperties: false },
    annotations: { readOnlyHint: true, consequentialHint: false, untrustedContentHint: false },
    execute: async () => {
      const product = await loadProduct();
      const variant = findCurrentVariant(product);
      return asResult({
        ok: true,
        product: product.title,
        handle: product.handle,
        url: window.location.href,
        selectedVariant: conciseVariant(product, variant),
        choices: listChoices(product),
      });
    },
  });

  register({
    name: 'select_variant',
    title: 'Produktvariante wählen',
    description: 'Wählt auf der geöffneten Produktseite eine verfügbare Variante nach Varianten-ID oder nach Breite, Möbelfarbe, Griffform und Beckenfarbe. Die sichtbare Seite wird aktualisiert.',
    inputSchema: {
      type: 'object',
      properties: {
        variantId: { type: 'string', description: 'Shopify-Varianten-ID, falls bekannt.' },
        width: { type: 'string', description: 'Gewünschte Breite, zum Beispiel 80 cm.' },
        furnitureColor: { type: 'string', description: 'Gewünschte Farbe des Unterschranks.' },
        gripType: { type: 'string', description: 'Gewünschte Griffform oder Grifffarbe.' },
        basinColor: { type: 'string', description: 'Gewünschte Farbe des Waschbeckens, falls auswählbar.' },
        color: { type: 'string', description: 'Allgemeine Farbauswahl bei Produkten ohne getrennte Farbfelder.' },
      },
      additionalProperties: false,
    },
    annotations: { readOnlyHint: false, consequentialHint: false, untrustedContentHint: false },
    execute: async (input) => {
      const product = await loadProduct();
      const requested = Object.fromEntries(Object.entries(input || {}).filter(([, value]) => String(value || '').trim()));
      if (!Object.keys(requested).length) {
        return asResult({ ok: false, error: 'Mindestens eine Varianten-ID oder eine gewünschte Eigenschaft angeben.', choices: listChoices(product) });
      }

      let matches = product.variants.slice();
      if (requested.variantId) matches = matches.filter((variant) => String(variant.id) === String(requested.variantId));
      for (const [key, value] of Object.entries(requested)) {
        if (key === 'variantId') continue;
        matches = matches.filter((variant) =>
          (product.options || []).some((option, index) =>
            optionKey(optionName(option)) === key && normalize(variant.options?.[index]) === normalize(value)
          )
        );
      }

      if (!matches.length) {
        return asResult({ ok: false, error: 'Keine passende Variante gefunden.', choices: listChoices(product) });
      }
      if (matches.length > 1) {
        return asResult({
          ok: false,
          error: 'Die Auswahl ist noch nicht eindeutig. Bitte weitere Eigenschaften angeben.',
          matches: matches.slice(0, 10).map((variant) => conciseVariant(product, variant)),
        });
      }

      const variant = matches[0];
      if (!variant.available) return asResult({ ok: false, error: 'Diese Variante ist derzeit nicht lieferbar.', variant: conciseVariant(product, variant) });

      const controls = Array.from(document.querySelectorAll('#MainContent variant-picker input, #MainContent variant-picker select'));
      variant.options.forEach((value) => {
        const control = controls.find((element) => element.value === value);
        if (!control) return;
        if (control instanceof HTMLInputElement && (control.type === 'radio' || control.type === 'checkbox')) {
          if (!control.checked) control.click();
        } else if (control instanceof HTMLSelectElement && control.value !== value) {
          control.value = value;
          control.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      const deadline = Date.now() + 6000;
      while (Date.now() < deadline && currentVariantId() !== String(variant.id)) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      if (currentVariantId() !== String(variant.id)) {
        window.location.assign(`${product.url}?variant=${variant.id}`);
        return null;
      }
      return asResult({ ok: true, selectedVariant: conciseVariant(product, variant), url: window.location.href });
    },
  });

  register({
    name: 'add_to_cart',
    title: 'In den Warenkorb legen',
    description: 'Legt die ausgewählte oder ausdrücklich angegebene lieferbare Produktvariante in der gewünschten Menge in den Shopify-Warenkorb und aktualisiert die sichtbare Warenkorbanzeige.',
    inputSchema: {
      type: 'object',
      properties: {
        variantId: { type: 'string', description: 'Shopify-Varianten-ID. Ohne Angabe wird die aktuell ausgewählte Variante verwendet.' },
        quantity: { type: 'integer', minimum: 1, maximum: 20, description: 'Gewünschte Stückzahl zwischen 1 und 20.' },
      },
      additionalProperties: false,
    },
    annotations: { readOnlyHint: false, consequentialHint: false, untrustedContentHint: false },
    execute: async ({ variantId, quantity = 1 }) => {
      const product = await loadProduct();
      const id = String(variantId || currentVariantId());
      const amount = Number(quantity);
      if (!Number.isInteger(amount) || amount < 1 || amount > 20) {
        return asResult({ ok: false, error: 'Die Stückzahl muss eine ganze Zahl zwischen 1 und 20 sein.' });
      }
      const variant = product.variants.find((item) => String(item.id) === id);
      if (!variant) return asResult({ ok: false, error: 'Die Varianten-ID gehört nicht zum geöffneten Produkt.' });
      if (!variant.available) return asResult({ ok: false, error: 'Diese Variante ist derzeit nicht lieferbar.' });

      await requestJson('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: amount }] }),
      });
      const cart = await requestJson('/cart.js');
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'zentrobad-webmcp',
          data: {
            source: 'zentrobad-webmcp',
            itemCount: cart.item_count,
            variantId: id,
            productId: String(product.id),
          },
        },
      }));
      return asResult({
        ok: true,
        added: conciseVariant(product, variant),
        quantity: amount,
        cartItemCount: cart.item_count,
        cartTotal: money(cart.total_price, cart.currency),
        cartUrl: new URL('/cart', window.location.origin).href,
      });
    },
  });
})();
